import _ from "lodash";
import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useReducer } from "react";

// Base (both analysis and ground data share this on initial load)
export type BaseAssessmentData = {
	id: string; // flightId or groundDatasetId
	date: string;
	enabled: boolean;
	checked: boolean;
};

// After a flight/analysis selection is made
export type AnalysisAssessmentData = BaseAssessmentData & {
	type: "analysis";
	name: string;
	displayName: string;
	assessmentType: string; // AnalysisType.id
	groundDataAssessmentId: "";
	quantifiedRegionTypeId: string;
};

// After a ground data selection is made
export type GroundDataAssessmentData = BaseAssessmentData & {
	type: "grounddata";
	name: string;
	displayName: string;
	assessmentType: string; // groundDataAssessmentId
	groundDataAssessmentId: string;
};

export type AssessmentData = (BaseAssessmentData | AnalysisAssessmentData | GroundDataAssessmentData)[];

// Shared base
export type BaseSelection = {
	id: string | null;
	name: string | null;
	displayName?: string;
	initialLoad?: boolean;
	isAnalysis: boolean;
	isGrowthCurveAnalysis: boolean;
};

export type AnalysisSelection = BaseSelection & {
	isAnalysis: true;
	isGrowthCurveAnalysis: false;
	isCurveModel: false;
	analysisValue: string; // AnalysisType.id
	analysisId?: string;
	analysisTypeId?: string; // AnalysisType.analysisTypeId — used by query logic to avoid looking up via assessmentTypes
	curveModelAnalysisId: null;
	quantifiedRegionTypeOptionId: string;
	assessments: AssessmentData;
};

export type GroundDataSelection = BaseSelection & {
	isAnalysis: false;
	isGrowthCurveAnalysis: false;
	isCurveModel: false;
	analysisValue: string; // groundDataAssessmentId
	curveModelAnalysisId: null;
	assessments: AssessmentData;
};

export type GrowthCurveSelection = BaseSelection & {
	isAnalysis: false;
	isGrowthCurveAnalysis: true;
	isCurveModel: true;
	analysisId: string;
	curveModelAnalysisId: string;
	analysisTypeId: string;
	analysisValue: null;
	startDate: string;
	endDate: string;
	checked: true;
	enabled: true;
	selected: boolean;
	assessments: null;
};

// Empty row added via addData()
export type EmptySelection = {
	id: null;
	name: null;
	assessments: [];
	isGrowthCurveAnalysis: false;
};

export type SummaryTableSelection = AnalysisSelection | GroundDataSelection | GrowthCurveSelection | EmptySelection;

interface AnalysisSettings {
	// options
	removeDamagedOrExcluded: boolean;
	useGdu: boolean;
	removeOutliers: boolean;
	outlierType: number;
	isPlotUniformity: boolean;
	selectedTreatmentMatrix: boolean[] | null;
	anovaType: "one-way" | "repeated";

	// ids
	assessmentId: string | null;
	flightId: string | null;
	selectedFlightIds: string[] | null;
	analysisId: string | null;
	analysisTypeId: string | null;
	quantifiedRegionTypeId: string | null;
	orthoImageTypeId: string | null;
	groundDataAssessmentId: string | null;
	groundDatasetId: string | null;
	curveModelAnalysisId: string | null;
	meanComparisonOptionId: string | null;
	alphaOptionId: string | null;

	// summary table
	summaryTablePresetId: string | null;
	summaryTableSelections: SummaryTableSelection[];
}

interface AnalysisDisplaySettings {
	assessmentName: string | null;
	analysisName: string | null;
	hasGdus: boolean;
	treatmentNames: string[];
	experimentalDesignId: string | null;
	isRCBD: boolean;
	collapseTable: boolean;
	assessmentFlightIds: string[];
}

type AnalysisAction =
	| {
			type: "change" | "initialize";
			value: Partial<AnalysisSettings>;
			silent?: boolean;
	  }
	| {
			type: "commit" | "reset";
	  }
	| {
			type: "change display";
			value: Partial<AnalysisDisplaySettings>;
	  };

interface AnalysisState {
	committed: AnalysisSettings;
	pending: AnalysisSettings;
	display: AnalysisDisplaySettings;
	isDirty: boolean;
	isInitialized: boolean;
	isAucOutdated: boolean;
	isLettersOutdated: boolean;
	actualSelectedFlights: string[];
}

interface AnalysisFunctions {
	changeSettings: (settings: Partial<AnalysisSettings>) => void;
	changeSettingsSilently: (settings: Partial<AnalysisSettings>) => void;
	changeDisplaySettings: (settings: Partial<AnalysisDisplaySettings>) => void;
	initializeSettings: (settings: Partial<AnalysisSettings>) => void;
	commitSettings: () => void;
	resetSettings: () => void;
}

// reserved for settings that involve a call to the database
const initialSettings: AnalysisSettings = {
	removeDamagedOrExcluded: true,
	useGdu: false,
	removeOutliers: false,
	outlierType: 3,
	isPlotUniformity: false,
	selectedTreatmentMatrix: null,
	anovaType: "one-way",

	assessmentId: null,
	flightId: null,
	selectedFlightIds: null,
	analysisId: null,
	alphaOptionId: null,
	meanComparisonOptionId: null,
	analysisTypeId: null,
	orthoImageTypeId: null,
	quantifiedRegionTypeId: null,
	groundDataAssessmentId: null,
	groundDatasetId: null,
	curveModelAnalysisId: null,

	summaryTablePresetId: "none",
	summaryTableSelections: [],
};

// reserved for settings that do not involve a call to the database
const initialDisplaySettings: AnalysisDisplaySettings = {
	assessmentName: null,
	analysisName: null,
	hasGdus: false,
	treatmentNames: [],
	isRCBD: true,
	experimentalDesignId: null,
	collapseTable: false,
	assessmentFlightIds: [],
};

const initialState: AnalysisState = {
	committed: initialSettings,
	pending: initialSettings,
	display: initialDisplaySettings,
	isDirty: false,
	isInitialized: false,
	isAucOutdated: false,
	isLettersOutdated: false,
	actualSelectedFlights: [],
};

const trialAnalysisReducer = (state: AnalysisState, action: AnalysisAction) => {
	switch (action.type) {
		case "change":
			return {
				...state,
				pending: { ...state.pending, ...action.value },
				isDirty: state.isDirty || !action.silent,
			};
		case "change display":
			return {
				...state,
				display: { ...state.display, ...action.value },
			};
		case "commit":
			return {
				...state,
				committed: state.pending,
				isDirty: false,
			};
		case "initialize":
			return {
				...state,
				pending: { ...state.pending, ...action.value },
				committed: { ...state.committed, ...action.value },
				isInitialized: true,
			};
		case "reset":
			return initialState;
	}
};

const TrialAnalysisContext = createContext<AnalysisState | null>(null);
const TrialAnalysisDispatchContext = createContext<AnalysisFunctions | null>(null);

const TrialAnalysisProvider = ({ children }: PropsWithChildren) => {
	const [analysis, dispatch] = useReducer(trialAnalysisReducer, initialState);

	const isAucOutdated = useMemo(() => {
		return _.xor(analysis.committed.selectedFlightIds, analysis.pending.selectedFlightIds).length !== 0;
	}, [analysis.committed.selectedFlightIds, analysis.pending.selectedFlightIds]);

	const isLettersOutdated = useMemo(() => {
		const checkMismatch = !_.isEqual(
			analysis.committed.selectedTreatmentMatrix,
			analysis.pending.selectedTreatmentMatrix,
		);
		const meanComparisonOptsMismatch =
			analysis.committed.meanComparisonOptionId !== analysis.pending.meanComparisonOptionId ||
			analysis.committed.alphaOptionId !== analysis.pending.alphaOptionId;

		return checkMismatch || meanComparisonOptsMismatch;
	}, [
		analysis.committed.alphaOptionId,
		analysis.committed.meanComparisonOptionId,
		analysis.committed.selectedTreatmentMatrix,
		analysis.pending.alphaOptionId,
		analysis.pending.meanComparisonOptionId,
		analysis.pending.selectedTreatmentMatrix,
	]);

	const actualSelectedFlights = useMemo(() => {
		return _.intersection(analysis.display.assessmentFlightIds, analysis.pending.selectedFlightIds);
	}, [analysis.display.assessmentFlightIds, analysis.pending.selectedFlightIds]);

	const changeSettings = useCallback(
		(settings: Partial<AnalysisSettings>) => dispatch({ type: "change", value: settings, silent: false }),
		[],
	);

	const changeSettingsSilently = useCallback(
		(settings: Partial<AnalysisSettings>) => dispatch({ type: "change", value: settings, silent: true }),
		[],
	);

	const changeDisplaySettings = useCallback(
		(settings: Partial<AnalysisDisplaySettings>) => dispatch({ type: "change display", value: settings }),
		[],
	);

	const commitSettings = useCallback(() => dispatch({ type: "commit" }), []);

	const resetSettings = useCallback(() => dispatch({ type: "reset" }), []);

	const initializeSettings = useCallback(
		(settings: Partial<AnalysisSettings>) => dispatch({ type: "initialize", value: settings }),
		[],
	);

	return (
		<TrialAnalysisContext.Provider value={{ ...analysis, isAucOutdated, isLettersOutdated, actualSelectedFlights }}>
			<TrialAnalysisDispatchContext.Provider
				value={{
					changeSettings,
					changeSettingsSilently,
					changeDisplaySettings,
					initializeSettings,
					commitSettings,
					resetSettings,
				}}
			>
				{children}
			</TrialAnalysisDispatchContext.Provider>
		</TrialAnalysisContext.Provider>
	);
};

const useTrialAnalysis = () => {
	const context = useContext(TrialAnalysisContext);

	if (context === null) {
		throw new Error("TrialAnalysisContext is not provided");
	}

	return context;
};

const useTrialAnalysisDispatch = () => {
	const context = useContext(TrialAnalysisDispatchContext);

	if (context === null) {
		throw new Error("TrialAnalysisDispatchContext is not provided");
	}

	return context;
};

export { TrialAnalysisProvider, useTrialAnalysis, useTrialAnalysisDispatch };
